import { GroupedSeries } from '@d3fc/d3fc-group';
import { VolumeCurveBucketedDataItem } from 'widgets/volumeCurve/data/volumeCurveDataBuckets.ts';
import { computeGroupedData } from 'utils/components/TimeSeriesChart/groupedChartData.ts';
import { computeTimeSeriesBarWidthPercentage } from 'utils/d3fc/chartSeries.ts';
import { VolumeCurveData } from 'widgets/volumeCurve/data/volumeCurveData.ts';
import { useMemo } from 'react';
import { TimeSeriesDisableRange } from 'utils/timeseries/TimeSeriesDisableRange.ts';

export type VolumeCurveChartBucketedDataItem = VolumeCurveBucketedDataItem & {
    historicalVolumeExcludingAuction: number;
    historicalAuctionAndTradedVolume: number;
    barWidthPercentage: number | undefined;
    cumulativeTradedVolume?: number;
    cumulativeHistoricalVolume: number;
};

export interface VolumeCurveChartData {
    bucketedData: VolumeCurveChartBucketedDataItem[];
    tradedVolumeBarData: GroupedSeries<
        Partial<{
            time: number;
            tradedVolume: number;
            historicalVolume: number;
            barWidthPercentage: number | undefined;
        }>
    >[];
    auctionVolumeBarData: GroupedSeries<
        Partial<{
            time: number;
            historicalAuctionAndTradedVolume: number;
            historicalVolume: number;
            barWidthPercentage: number | undefined;
        }>
    >[];
    chartMax: number;
}

export function buildVolumeCurveChartData(
    data: VolumeCurveData
): VolumeCurveChartData {
    const { bucketedData, tickSize } = data;

    const bucketSizeInMins = tickSize;

    const bucketsMax = (b: VolumeCurveBucketedDataItem[]) =>
        Math.max(
            ...b.map((p) =>
                Math.max(
                    p.historicalVolume, // Includes auction
                    p.tradedVolume
                )
            )
        );

    const chartMax = bucketsMax(bucketedData);

    const breaksInTrading: TimeSeriesDisableRange[] = [];

    const enrichedBuckets: VolumeCurveChartBucketedDataItem[] =
        bucketedData.map((x, currentIndex) => ({
            ...x,
            _i: x.midTime / 60000,
            time: x.midTime,
            historicalAuctionAndTradedVolume: x.historicalVolume,
            historicalVolumeExcludingAuction:
                (x.historicalVolume ?? 0) - (x.auctionVolume ?? 0),
            cumulativeTradedVolume: x.cumulativeTradedVolume,
            cumulativeHistoricalVolume: x.cumulativeHistoricalVolume,
            currentIndex,
            barWidthPercentage: computeTimeSeriesBarWidthPercentage(
                currentIndex,
                bucketedData,
                bucketSizeInMins ?? 1,
                breaksInTrading
            ),
        }));

    const tradedVolumeBarData: VolumeCurveChartData['tradedVolumeBarData'] =
        computeGroupedData(
            enrichedBuckets,
            (d) => ({
                time: d.time,
                tradedVolume: d.tradedVolume,
                historicalVolumeExcludingAuction:
                    d.historicalVolumeExcludingAuction,
                barWidthPercentage: d.barWidthPercentage,
            }),
            ['historicalVolumeExcludingAuction', 'tradedVolume']
        );

    const auctionVolumeBarData: VolumeCurveChartData['auctionVolumeBarData'] =
        computeGroupedData(
            enrichedBuckets,
            (d) => ({
                time: d.time,
                historicalAuctionAndTradedVolume:
                    d.auctionVolume !== undefined && d.auctionVolume !== 0
                        ? d.historicalVolume
                        : undefined,
                historicalVolumeExcludingAuction: 0, // dummy
                barWidthPercentage: d.barWidthPercentage,
            }),
            [
                'historicalAuctionAndTradedVolume',
                'historicalVolumeExcludingAuction',
            ]
        );

    return {
        chartMax,
        bucketedData: enrichedBuckets,
        tradedVolumeBarData,
        auctionVolumeBarData,
    };
}

export function useVolumeCurveChartData(data: VolumeCurveData) {
    return useMemo(() => buildVolumeCurveChartData(data), [data]);
}
